<template>
    <div
        v-if="call"
        class="chat-container"
    >
        <div class="chat-header gap-2">
            <div class="flex items-center justify-between">
                <div class="flex items-center w-2/3 gap-1.5">
                    <div>
                        <redesign-base-icon
                            :name="'GripVertical'"
                            class="text-gray-550"
                        />
                    </div>
                    <div class="truncate overflow-ellipsis">
                        {{ call.callername || call.CallerID }}
                    </div>
                </div>
                <div v-show="call.callStatus === 'Talking'">
                    <div class="flex items-center gap-2 border h-5 border-active-elements rounded-lg p-1.5">
                        <span class="w-1 h-1 bg-primary rounded-full" />
                        <span class="live-text">
                            {{ $t('general.live') }}
                        </span>
                    </div>
                </div>
                <div
                    class="cursor-pointer"
                    @click="removePopup(call.IvrUniqueID, botId)"
                >
                    <redesign-base-icon
                        name="X"
                    />
                </div>
            </div>
        </div>
        <div ref="searchContainer">
            <div
                v-show="searchMode"
                class="flex items-center justify-between px-4 py-2 animate-slide-down"
                style="box-shadow: 0 4px 8px 0 rgba(38, 49, 72, 0.08);"
            >
                <div class="">
                    <redesign-base-input
                        v-model="searchValue"
                        :prefix-icon="'Search'"
                        :placeholder="'search'"
                        class="rounded"
                        size="mini"
                        is-rounded
                        @input="findMatches"
                    />
                </div>
                <div
                    v-if="matchIndexes?.length"
                    class="flex gap-1"
                >
                    <span class="text-primary">
                        {{ currentMatchIndex !== null ? currentMatchIndex + 1 : 0 }}
                    </span>
                    <span>
                        {{ $t('general.of') }}
                    </span>
                    <span>
                        {{ matchIndexes.length }}
                    </span>
                    <div
                        class="cursor-pointer"
                        @click="prevMatch"
                    >
                        <redesign-base-icon
                            :name="'ChevronUp'"
                        />
                    </div>
                    <div
                        class="cursor-pointer"
                        @click="nextMatch"
                    >
                        <redesign-base-icon
                            :name="'ChevronDown'"
                        />
                    </div>
                </div>
            </div>
            <div
                v-show="!searchMode"
                class="flex items-center justify-between px-4 py-2 animate-slide-down"
                style="box-shadow: 0 4px 8px 0 rgba(38, 49, 72, 0.08);"
            >
                <div class="flex gap-1.5">
                    <div
                        class="chat-action-btn"
                        @click="scrollToTop"
                    >
                        <redesign-base-icon :name="'ArrowUp'" />
                    </div>
                    <div
                        class="chat-action-btn chat-action-btn--autoscroll"
                        @click="enableAutoScroll = !enableAutoScroll"
                    >
                        <redesign-base-icon
                            :name="'Mouse'"
                            :class="{'text-active-elements' : enableAutoScroll}"
                        />
                    </div>
                </div>
                <div
                    class="cursor-pointer"
                    @click="searchMode = true"
                >
                    <RedesignTag :tag-content="[{text: 'Search', iconName: 'Search'}]" />
                </div>
            </div>
        </div>
        <div
            ref="chatMessageContainer"
            class="messages-block"
        >
            <div
                v-for="(event, index) in call.eventsData"
                :key="`${event.Timestamp}_${index}`"
                :class="{
                    'from-bot': !isUser(event),
                    'from-user': isUser(event),
                    'message-wrapper' : !(event.Type === 'CallStarted' || event.Type === 'CallEnded')
                }"
                :data-index="index"
            >
                <div v-if="event.Type === 'CallStarted' || event.Type === 'CallEnded'">
                    <span class="meta text-center justify-center">
                        {{ formatTime(event.Timestamp_UTC_CLIENT) }}
                    </span>
                    <div class="text-center text-xs py-1.5 bg-gray-300 text-gray-550 w-2/3 mx-auto my-2">
                        {{ event.Type }}
                    </div>
                </div>
                <div
                    v-else
                    class="meta-wrap"
                >
                    <div class="meta">
                        <span class="sender">
                            {{ event.Role }}
                        </span>
                        <span class="time">
                            {{ formatTime(event.Timestamp_UTC_CLIENT) }}
                        </span>
                    </div>

                    <div
                        v-show="event.Transcript"
                        class="bubble"
                        v-html="highlightMatch(event.Transcript)"
                    />
                    <!--                    <div v-show="event.Type === 'ToolExecuted'">-->
                    <!--                      <redesign-base-icon name=""></redesign-base-icon>-->
                    <!--                    </div>-->
                    <!--                    <div v-show="event.Type === 'ToolExecuted'">-->
                    <!--                      <redesign-base-icon name=""></redesign-base-icon>-->
                    <!--                    </div>-->
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignTag from '@/components/RedesignBaseComponents/RedesignTag.vue'
import { removePopup } from '@/composables/useChatsData'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import { useVoiceBotsState } from '@/composables/useVoiceBots'
import { VoicebotEventDataCallEventsData } from '@voicenter-team/events-sdk'


const props = defineProps<{
    callId: string
    botId: number
}>()

const chatMessageContainer = ref<HTMLElement | null>(null)
const searchMode = ref(false)
const enableAutoScroll = ref(true)
const searchValue = ref('')
const searchContainer = ref<HTMLElement | null>(null)
const matchIndexes = ref<number[]>([])
const currentMatchIndex = ref<null | number>(null)
const{ allBotsWithCalls } = useVoiceBotsState()


const call = computed(() => {
    const bot = allBotsWithCalls.value.find(b => b.VoicebotID === props.botId)
    return bot?.Calls.find(c => c.IvrUniqueID === props.callId)
})

const findMatches = () => {
    const query = searchValue.value.trim()

    if (!query) {
        matchIndexes.value = []
        currentMatchIndex.value = null
        return
    }
    matchIndexes.value = call.value?.eventsData
        .map((event, idx) => (event.Transcript?.includes(searchValue.value) ? idx : -1))
        .filter(idx => idx !== -1) ?? []

    currentMatchIndex.value = matchIndexes.value?.length ? 0 : null
    if (currentMatchIndex.value !== null) {
        scrollToMessage(matchIndexes.value[currentMatchIndex.value])
    }
}

const handleClickOutside = (event: MouseEvent) => {
    if (
        searchMode.value &&
        searchContainer.value &&
        !searchContainer.value.contains(event.target as Node)
    ) {
        searchMode.value = false
        searchValue.value = ''
    }
}

const scrollToMessage = (index: number) => {
    const container = chatMessageContainer.value
    if (!container) {
        return
    }
    const el = container.querySelector(`.message-wrapper[data-index="${index}"]`)
    if (el) {
        el.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        })
    }
}

const scrollToTop = () => {
    const container = chatMessageContainer.value
    if (!container) return

    container.scrollTo({
        top: 0,
        behavior: 'smooth'
    })
}

const nextMatch = () => {
    if (!matchIndexes.value.length) return
    currentMatchIndex.value = currentMatchIndex.value !== null ? (currentMatchIndex.value + 1) % matchIndexes.value.length : 0
    scrollToMessage(matchIndexes.value[currentMatchIndex.value])
}

const prevMatch = () => {
    if (!matchIndexes.value.length) return
    currentMatchIndex.value = currentMatchIndex.value !== null ? (currentMatchIndex.value - 1 + matchIndexes.value.length) % matchIndexes.value.length : 0
    scrollToMessage(matchIndexes.value[currentMatchIndex.value])
}

function isUser (event: VoicebotEventDataCallEventsData) {
    return String(event.Role || '').toLowerCase() === 'user'
}

const highlightMatch = (text: string) => {
    const query = searchValue.value.trim()
    if (!query || !text) return text

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedQuery})`, 'gi')

    return text.replace(regex, '<span class="bg-amber-500 text-white">$1</span>')
}

function formatTime (ts: number) {
    const date = new Date(ts)
    return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
    })
}

watch(
    () => call.value?.eventsData,
    async () => {
        if (enableAutoScroll.value && chatMessageContainer.value) {
            await nextTick()
            chatMessageContainer.value.scrollTo({
                top: chatMessageContainer.value.scrollHeight,
                behavior: 'smooth',
            })
        }
    },
    { deep: true }
)

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>

<style lang="scss" scoped>
.chat-container {
    @apply flex h-full;
    flex-direction: column;
    background: var(--mainBgColor-color);
    border: 1px solid var(--colorWidgetBorder-color);
    border-radius: 10px;
}

.live-text {
  @apply text-xs text-active-elements;
  font-size: 11px;
}

.chat-action-btn {
  @apply cursor-pointer w-6 h-6 flex items-center justify-center;
  border-radius: 50%;
  border: 1px solid color-mix(in srgb, var(--primary-actions) 10%, transparent);
  background-color:
      color-mix(in srgb, var(--primary-actions) 10%, transparent);
}
.chat-action-btn--autoscroll {
  background-color:
      color-mix(in srgb, var(--primary-actions) 10%, transparent);
}
.chat-header {
    padding: 8px 12px;
    border-bottom: 1px solid var(--stroke, #E5E7EB);
    background-color:
      color-mix(in srgb, var(--primary-actions) 5%, transparent);
}

.messages-block {
    @apply py-1;
    height: 360px;
    overflow-y: scroll;
}

.message-wrapper {
    display: flex;
    padding: 0 15px;
    margin-bottom: 15px;
}

.message-wrapper.from-user {
    text-align: right;
    justify-content: flex-end;
}

.meta-wrap {
  width: max-content;
  max-width: 90%;
}
.meta {
    @apply flex items-center text-gray;
    font-size: 0.75rem;
    line-height: 1.5;
    margin-bottom: 4px;
}

.sender {
    @apply px-2 font-semibold mr-4 relative block;
    &::after {
      position: absolute;
      width: 2px;
      height: 2px;
      color: grey;
      left: 50%;
      top: 50%;
    }
}

.bubble {
    @apply py-1 px-2 font-normal;
    border-radius: 12px;
    font-size: 12px;
    line-height: 150%;
    box-shadow: 0 2px 4px #00000014, 0 4px 6px #2675ff1f, 0 6px 9px #2675ff1f;
}

.from-user .bubble {
    background-color: var(--primary-actions);
    color: white;
    border-bottom-right-radius: 0;
}

.from-bot .bubble {
    background-color: color-mix(in srgb, var(--primary-actions) 5%, transparent);
    color: var(--mainTextColor-color);
    border-bottom-left-radius: 0;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-down {
  animation: slideDown 0.3s ease;
}
</style>


